import MaterialsService, { updateIndex } from "@/services/MaterialsService";
import { uploadItemsByZip, createOrUpdateMaterialsReview, getMaterialAvaliationReasons, getMaterialsReview, getMaterialAvaliationReasonsInGroups } from "@/services/MaterialsService";
import { validateRestrictionData } from "@/utils/helpers";

export default {
    state() {
        return {
            material: null,
            materialsData: [],
            materialsReview: [],
            disciplineBeingEdited: null,
            materialItemSelectPage: 0,
            materialsPerPage: 5,
            materialAvaliationReasons: []
        }
    },

    getters: {
        getMaterialsPerPage: (state) => state.materialsPerPage,
        getMaterialsPage: (state) => state.materialItemSelectPage,
        getMaterialAvaliationReasons: (state) => state.materialAvaliationReasons,
        getMaterialsReview: (state) => state.materialsReview,
        getMaterialId: (state) => state?.material?.id || null,
        getMaterialData: (state) => state.material,
        getDisciplineBeingEdited: (state) => state.disciplineBeingEdited,
        getMaterialsData: (state) => state.materialsData,
        getMaterialName: (state) => state.material.name,
        getMaterialColor: (state) => state.material.color,
        getMaterialDisciplineId: (state) => state.material.disciplineId,
        getMaterialPagesAvaliable: (state) => {
            const materialsAmountRendered = (state.materialItemSelectPage + 1) * state.materialsPerPage
            return Boolean(Array.isArray(state.materialsData) && materialsAmountRendered < state.materialsData.length)
        },
        getMaterialsPaginated: (state) => {
            if (Array.isArray(state.materialsData) && state.materialsData.length > 0) {
                const indexToStart = state.materialItemSelectPage * state.materialsPerPage
                const indexToEnd = indexToStart + state.materialsPerPage
                let materialsPaginated = state.materialsData.slice(indexToStart, indexToEnd)

                if (materialsPaginated.length < state.materialsPerPage) {
                    const pendentMaterials = state.materialsPerPage - materialsPaginated.length
                    for (let i = 0; i < pendentMaterials; i++) {
                        materialsPaginated.push(null)
                    }
                }

                return materialsPaginated
            }
            return []
        },
    },

    mutations: {
        handleMaterialItemSelectPage(state, payload) {
            if (!payload.action) return

            switch (payload.action) {
                case 'reset':
                    state.materialItemSelectPage = 0
                    break;
                case 'increment':
                    if (payload?.materialPagesAvaliable) {
                        state.materialItemSelectPage++
                    }
                    break;
                case 'decrement':
                    if (state.materialItemSelectPage > 0) {
                        state.materialItemSelectPage--
                    }
                    break;
            }
        },

        setMaterialAvaliationReasons(state, data) {
            state.materialAvaliationReasons = data || []
        },

        setMaterialsReview(state, data) {
            data && (state.materialsReview = data)
        },

        setNewMaterialDataBasedInIndexes(state, data) {
            data && (state.materialsData = data)
        },

        setDisciplineBeingEdited(state, data) {
            state.disciplineBeingEdited = data
        },

        setMaterialData(state, data) {
            state.material = data || null
        },
        setMaterialsData(state, data) {
            state.materialsData = data
        },

        deleteMaterial(state, materialId) {
            const materialIndex = state.materialsData.findIndex((m) => m.id === materialId)
            state.materialsData.splice(materialIndex, 1)
            state?.material?.id === materialId && (state.material = null)
        },

        createMaterial(state, data) {
            data && (state.materialsData.push(data))
        },

        duplicateMaterial(state, data) {
            data && (state.materialsData.splice(data.index, 0, data))
        },

        updateMaterial(state, data) {
            const materialIndex = state.materialsData.findIndex((m) => m.id === data.id)
            if (materialIndex !== -1) {
                state.materialsData[materialIndex] = data
            }
        },

        updateMaterialProgress(state, data) {
            const materialIndex = state.materialsData.findIndex((m) => m.id === data.materialId)
            if (materialIndex !== -1) {
                state.materialsData[materialIndex].progress = data.material_progress
            }
        },

        insetMaterialsCreatedByZip(state, data) {
            data && (state.materialsData = [...state.materialsData, ...data])
        },

        removeMaterialRestrictionToConsumers(state, data) {
            const { validPayload, contentManipulatedId, consumersId } = validateRestrictionData(data)
            if (!validPayload) return

            const materialIndex = state.materialsData.findIndex((material) => material?.id === contentManipulatedId)
            if (materialIndex !== -1) {
                const material = state.materialsData[materialIndex]

                material.restricted_consumers = material.restricted_consumers.filter((consumerId) => !consumersId.includes(consumerId))
            }
        },

        restrictMaterialToConsumers(state, data) {
            const { validPayload, contentManipulatedId, consumersId } = validateRestrictionData(data)
            if (!validPayload) return

            const materialIndex = state.materialsData.findIndex((material) => material?.id === contentManipulatedId)
            if (materialIndex !== -1) {
                const material = state.materialsData[materialIndex]
                !material.restricted_consumers && (material.restricted_consumers = [])

                const newConsumers = consumersId.filter(id => !material.restricted_consumers.includes(id))
                material.restricted_consumers = [
                    ...material.restricted_consumers,
                    ...newConsumers
                ]
            }
        }
    },

    actions: {
        handleMaterialItemSelectPage({ commit }, payload) {
            commit('handleMaterialItemSelectPage', payload)
        },

        setNewMaterialDataBasedInIndexes({ commit }, payload) {
            commit('setNewMaterialDataBasedInIndexes', payload)
        },

        updateMaterialProgress({ commit }, payload) {
            commit('updateMaterialProgress', payload)
        },

        setMaterialData({ commit }, data) {
            commit('setMaterialData', data)
        },

        setDisciplineBeingEdited({ commit }, payload) {
            commit('setDisciplineBeingEdited', payload)
        },

        async createMaterial({ commit, dispatch }, payload) {
            try {
                dispatch('setLoadingState', { loadignState: true, loadingView: 'materialView' })
                const response = await MaterialsService.createMaterial(payload.material, payload.userToken);
                commit('createMaterial', response.data)
                commit('resetMaterial');
                dispatch('toastfy/activeSuccesNotification', { text: "Material criado com sucesso!" })
                return response.data;
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: `Erro ao criar material, tente novamente!` })
            } finally {
                dispatch('setLoadingState', { loadignState: false })
            }
        },

        async findMaterial({ commit, dispatch }, payload) {
            try {
                dispatch('setLoadingState', { loadignState: true, loadingView: 'materialView' })
                const response = await MaterialsService.findMaterial(payload.materialId, payload.userToken);
                commit('setMaterialData', response.data);
                return response.data;
            } catch {
                dispatch('toastfy/activeErrorNotification', { text: `Erro ao requisitar items do material, tente novamente!` })
            } finally {
                dispatch('setLoadingState', { loadignState: false })
            }
        },

        async updateMaterial({ commit, dispatch }, payload) {
            try {
                dispatch('setLoadingState', { loadignState: true, loadingView: 'materialView' })
                const response = await MaterialsService.updateMaterial(payload.material, payload.userToken);
                commit("updateMaterial", response.data)
                dispatch('toastfy/activeSuccesNotification', { text: "Material editado com sucesso!" })

                return response.data;
            } catch {
                dispatch('toastfy/activeErrorNotification', { text: `Erro ao atualizar material, tente novamente!` })
            } finally {
                dispatch('setLoadingState', { loadignState: false })
            }
        },

        async deleteMaterial({ commit, dispatch, state }, payload) {
            try {
                dispatch('setLoadingState', { loadignState: true, loadingView: 'materialView' })
                const response = await MaterialsService.deleteMaterial(payload.materialId, payload.userToken);
                payload.materialId === state?.material?.id && (await dispatch('resetMaterialItensData'))
                commit('deleteMaterial', payload.materialId)
                dispatch('toastfy/activeSuccesNotification', { text: "Material excluido com sucesso!" })
                return response.data;
            } catch {
                dispatch('toastfy/activeErrorNotification', { text: `Erro ao deletar material, tente novamente!` })
            } finally {
                dispatch('setLoadingState', { loadignState: false })
            }
        },

        async duplicateMaterial({ commit, dispatch }, payload) {
            try {
                dispatch('setLoadingState', { loadignState: true, loadingView: 'materialView' })
                const response = await MaterialsService.duplicateMaterial(payload.materialId, payload.userToken);
                commit("duplicateMaterial", response.data)
                dispatch('toastfy/activeSuccesNotification', { text: "Material duplicado com sucesso!" })
                return response.data;
            } catch {
                dispatch('toastfy/activeErrorNotification', { text: `Erro ao duplicar material, tente novamente!` })
            } finally {
                dispatch('setLoadingState', { loadignState: false })
            }
        },

        async getMaterials({ commit, dispatch }, payload) {
            try {
                const response = await MaterialsService.getMaterials(payload.data, payload.userToken);

                commit('setMaterialsData', response.data);
                return response.data;
            } catch {
                dispatch('toastfy/activeErrorNotification', { text: `Erro ao requisitar materiais, tente novamente!` })
            } finally {
                if (payload.skeletonKey) {
                    dispatch('skeleton/setLoadingState', { key: payload.skeletonKey, value: false }, { root: true })
                }
            }
        },

        async updateIndex({ commit, dispatch }, payload) {
            try {
                const response = await updateIndex(payload.data, payload.userToken);
                return response.data;
            } catch {
                dispatch('toastfy/activeErrorNotification', { text: `Erro ao atualizar posição de material, tente novamente!` })
            }
        },

        async uploadItemsByZip({ commit, dispatch }, payload) {
            try {
                dispatch('setLoadingState', { loadignState: true, loadingView: 'materialsView' }, { root: true });
                const response = await uploadItemsByZip(payload)
                commit('insetMaterialsCreatedByZip', response?.imported_files?.materials || [])
                dispatch('toastfy/activeSuccesNotification', { text: response.message || 'Importação Realizada!' })
                return {
                    requestStatus: true,
                }
            } catch (error) {

                return {
                    requestStatus: false,
                    errors: error?.invalid_files,
                    message: error?.message
                }
            } finally {
                dispatch('setLoadingState', { loadignState: false }, { root: true });
            }
        },

        async createOrUpdateMaterialsReview({ commit, dispatch }, payload) {
            try {
                const response = await createOrUpdateMaterialsReview(payload)
                commit('setMaterialsReview', response?.data?.feedbacks || [])
                dispatch('toastfy/activeSuccesNotification', { text: 'Avaliação concluída! Obrigado pelo seu feedback' })
                return true
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },

        async getMaterialsReview({ commit, dispatch }, payload) {
            try {
                const response = await getMaterialsReview(payload)
                commit('setMaterialsReview', response?.data?.feedbacks || [])
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },

        async getMaterialAvaliationReasons({ commit, dispatch }) {
            try {
                const response = await getMaterialAvaliationReasons()
                commit('setMaterialAvaliationReasons', response)
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },

        async getMaterialAvaliationReasonsInGroups({ commit, dispatch }) {
            try {
                const response = await getMaterialAvaliationReasonsInGroups()
                commit('setMaterialAvaliationReasons', response)
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },

        async restrictMaterialToConsumers({ commit, dispatch }, payload) {
            try {
                await MaterialsService.restrictMaterialToConsumers(payload)
                commit('restrictMaterialToConsumers', payload)
                dispatch('toastfy/activeSuccesNotification', { text: "Restrição aplicada a consumidores com sucesso!" })
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },

        async removeMaterialRestrictionToConsumers({ commit, dispatch }, payload) {
            try {
                await MaterialsService.removeMaterialRestrictionToConsumers(payload)
                commit('removeMaterialRestrictionToConsumers', payload)
                dispatch('toastfy/activeSuccesNotification', { text: "Restrição removida a consumidores com sucesso!" })
            } catch (error) {
                dispatch('toastfy/activeErrorNotification', { text: error })
            }
        },
    }
}
